import { autoUpdate, computePosition, flip, hide, offset, shift } from "@floating-ui/dom";
import type { Editor } from "@tiptap/core";
import type { ParentComponent } from "solid-js";
import { createEffect, createMemo, createSignal, Show, untrack } from "solid-js";
import { isNil } from "lodash-es";
import { useWysiwyg } from "../WysiwygContext";
import { getNearestOverflowAncestor } from "@zag-js/dom-query";
import { contain } from "../../../utils/floatingUiUtil";

type BubbleMenuProps = {
  referenceElement: (props: { editor: Editor; from: number; to: number }) => Element | null;
};

export const BubbleMenu: ParentComponent<BubbleMenuProps> = (props) => {
  const { editor } = useWysiwyg();
  const [referenceElement, setReferenceElement] = createSignal<Element | null>();
  let bubbleMenuRef!: HTMLDivElement;
  let autoUpdateCleanup: (() => void) | null = null;

  const scrollContainer = createMemo(() => getNearestOverflowAncestor(editor().view.dom));

  createEffect(() => {
    const { ranges } = editor().state.selection;
    const from = Math.min(...ranges.map((range) => range.$from.pos));
    const to = Math.max(...ranges.map((range) => range.$to.pos));

    // When clicking on a element inside the bubble menu the editor "blur" event
    // is called and the bubble menu item is focussed. In this case we should
    // consider the menu as part of the editor and keep showing the menu
    const isChildOfMenu = bubbleMenuRef.contains(document.activeElement);
    const hasEditorFocus = editor().view.hasFocus() || isChildOfMenu;
    if (hasEditorFocus && editor().isEditable) {
      const reference = props.referenceElement({ editor: editor(), from, to });
      if (!isNil(reference)) {
        setReferenceElement(reference.querySelector("[data-node-view-anchor]") ?? reference);
      } else {
        setReferenceElement(reference);
      }
    } else {
      setReferenceElement(null);
    }
  });

  createEffect(() => {
    const reference = referenceElement();
    if (!isNil(autoUpdateCleanup)) {
      autoUpdateCleanup();
      autoUpdateCleanup = null;
    }

    if (!isNil(reference)) {
      autoUpdateCleanup = autoUpdate(reference, bubbleMenuRef, () => {
        computePosition(reference, bubbleMenuRef, {
          placement: "bottom",
          middleware: [
            offset(4),
            flip({ boundary: untrack(() => scrollContainer()) }),
            shift({ padding: 8 }),
            contain(
              untrack(() => scrollContainer()),
              { padding: 8 },
            ),
            hide({ boundary: untrack(() => scrollContainer()) }),
          ],
        }).then(({ x, y, middlewareData }) => {
          Object.assign(bubbleMenuRef.style, {
            left: `${x}px`,
            top: `${y}px`,
          });

          if (!isNil(middlewareData.hide?.referenceHidden)) {
            Object.assign(bubbleMenuRef.style, {
              visibility: middlewareData.hide.referenceHidden ? "hidden" : "visible",
            });
          }
        });
      });
    }
  });

  return (
    <div ref={bubbleMenuRef} class="fixed z-10">
      <Show when={!isNil(referenceElement())}>
        <div class="inline-block h-9.5 rounded-sm bg-surface-default p-1 shadow-elevation-light-3" role="toolbar">
          {props.children}
        </div>
      </Show>
    </div>
  );
};
